import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LocaleContext, { useLocale } from './LocaleContext.jsx';
import { useLocalePreference } from '../hooks/useLocalePreference.js';
import { readSession } from '../_helpers/session.js';

/**
 * Layers the signed-in listener's account locale preference (ENG-379) over
 * the device copy `LocaleProvider` keeps. Sits inside the Router, like
 * NotificationsProvider, so it can re-check the session on every navigation:
 * sessionStorage fires no events, but every sign-in and sign-out ends in a
 * route change, and that is the moment the account preference is fetched
 * (and applied, mirrored to the device) or dropped.
 *
 * Everything under it reads the same `useLocale()` shape as before with two
 * differences: `setLocale` also saves the choice to the account while a
 * session is held, and `preference` reports whether choices reach the
 * account (`status`), a save in flight (`saving`) and the latest failed save
 * with its `retry` (`saveError`). `LocaleProvider` itself stays outside the
 * Router (App reads `t` there), so the device layer is untouched.
 */
export function LocalePreferenceSync({ children }) {
  const location = useLocation();
  const [hasSession, setHasSession] = useState(readSession);

  useEffect(() => {
    setHasSession(readSession());
  }, [location.pathname]);

  const device = useLocale();
  const { status, saving, saveError, choose } = useLocalePreference({
    enabled: hasSession,
    locale: device.locale,
    setLocale: device.setLocale,
  });

  const value = useMemo(
    () => ({ ...device, setLocale: choose, preference: { status, saving, saveError } }),
    [device, choose, status, saving, saveError]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export default LocalePreferenceSync;
